import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { Button, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import BetterBreadcrumb from 'src/components/Common/BetterBreadCrumb';
import { useCreateKbQuestionMutation } from 'src/rtk/features/chatbot/chatbotApi';
import * as Yup from 'yup';

const schema = Yup.object({
  question: Yup.string().required('Question is required'),
  answer: Yup.string().required('Answer is required'),
  status: Yup.string().oneOf(['draft', 'published']).default('draft'),
});

const initialValues = {
  question: '',
  answer: '',
  status: 'draft' as 'draft' | 'published',
};

const KnowledgeBaseCreatePage = () => {
  document.title = 'New Question - Knowledge Base | BCPS';
  const history = useHistory();

  const [createQuestion, { isLoading }] = useCreateKbQuestionMutation();

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        await createQuestion({
          question: values.question,
          answer: values.answer,
          status: values.status,
        }).unwrap();
        toast.success('Question created.');
        history.push('/admin/knowledge-base/questions');
      } catch (e: any) {
        const apiErrors = e?.data?.errors;
        if (apiErrors && typeof apiErrors === 'object') {
          const message = Object.values(apiErrors).flat().join(' ');
          toast.error(message || e?.data?.message || 'Failed to create.');
        } else {
          toast.error(e?.data?.message || 'Failed to create.');
        }
      }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue } = formik;

  return (
    <div className="page-content">
      <Container fluid>
        <BetterBreadcrumb
          title="New Question"
          breadcrumbItem={[
            { link: '/admin/knowledge-base/questions', name: 'Question & Answer' },
          ]}
        />
        <Card>
          <CardBody>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label for="question">Question *</Label>
                    <Input
                      id="question"
                      name="question"
                      type="textarea"
                      rows={3}
                      value={values.question}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={!!(errors.question && touched.question)}
                    />
                    {errors.question && touched.question && (
                      <div className="text-danger small">{errors.question}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={12}>
                  <FormGroup>
                    <Label for="answer">Answer *</Label>
                    <Input
                      id="answer"
                      name="answer"
                      type="textarea"
                      rows={5}
                      value={values.answer}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={!!(errors.answer && touched.answer)}
                    />
                    {errors.answer && touched.answer && (
                      <div className="text-danger small">{errors.answer}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label for="status">Status</Label>
                    <Input
                      id="status"
                      name="status"
                      type="select"
                      value={values.status}
                      onChange={(e) => setFieldValue('status', e.target.value)}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </Input>
                  </FormGroup>
                </Col>

              </Row>
              <div className="d-flex gap-2 mt-3">
                <Button type="submit" color="primary" disabled={isLoading} className='px-4'>
                  {isLoading ? 'Saving...' : 'Save'}
                </Button>
                <Button type="button" color="secondary" outline onClick={() => history.push('/admin/knowledge-base/questions')} className='px-4'>
                  Cancel
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default KnowledgeBaseCreatePage;
